/**
 * The planning board a coach last had open on the Player Database, remembered in this browser per
 * user so "Plan a roster" reopens it. A convenience like `boardMemory.ts`: every access is wrapped,
 * and blocked or cleared storage just means the first board is opened.
 *
 * It is also the coach's needs board: the "Your needs" card shows its needs, and every
 * `/api/directory/` request sends it as `X-Needs-Board` (`services/api.ts`), so Worth to you is
 * weighed by the same board. A change of board is announced to `subscribePlanBoard` listeners, which
 * is how the lists showing Worth to you know to read again (`useNeedsBoardVersion`).
 */
const keyFor = (userId: string) =>
  `rosterpilot.playerDatabase.planBoard.v1.${userId}`;

export const readPlanBoard = (
  userId: string | null | undefined,
): string | null => {
  if (!userId) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    return raw && raw.length <= 64 ? raw : null;
  } catch {
    return null;
  }
};

const listeners = new Set<() => void>();
let version = 0;

/** Bumped each time the remembered board changes, for `useSyncExternalStore`. */
export const planBoardVersion = (): number => version;

/** Called after the remembered board changes. Returns an unsubscribe function. */
export const subscribePlanBoard = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const writePlanBoard = (
  userId: string | null | undefined,
  boardId: string | null,
): void => {
  if (!userId) return;
  const before = readPlanBoard(userId);
  try {
    if (boardId) window.localStorage.setItem(keyFor(userId), boardId);
    else window.localStorage.removeItem(keyFor(userId));
  } catch {
    // Storage blocked or full: the button opens the first board next time.
  }
  // Compared with what is stored now, so a write storage refused announces nothing.
  if (readPlanBoard(userId) === before) return;
  announce();
};

const announce = () => {
  version += 1;
  for (const listener of [...listeners]) listener();
};

/**
 * The needs on a board were saved (Roster planning's setup). Worth to you reads them, so the lists
 * read again just as they do after a change of board.
 */
export const announcePlanBoardNeedsSaved = (): void => announce();

const contentListeners = new Set<() => void>();

/**
 * Called when a board's rows or setup changed somewhere other than the planning tray (the AI search's Confirm), so an
 * open tray reads its board and board list again. Returns an unsubscribe function.
 */
export const subscribePlanBoardContents = (listener: () => void): (() => void) => {
  contentListeners.add(listener);
  return () => {
    contentListeners.delete(listener);
  };
};

/** A board was written outside the tray: the tray reads again, and the lists read again as after a needs save. */
export const announcePlanBoardContentsChanged = (): void => {
  for (const listener of [...contentListeners]) listener();
  announce();
};
